// Copyright (c) 2026 Vigeos SASU. Tous droits réservés.
/**
 * Recherche du back-office : une seule règle pour tous les écrans, afin qu'un
 * même texte rende les mêmes lignes partout.
 *
 *   1. accents et casse sont ignorés : « Nîmes » se trouve en tapant « nimes » ;
 *   2. plusieurs mots se cumulent (ET, pas OU) : « bellevue 33 » ne rend que les
 *      lignes qui portent les deux, on affine en ajoutant un mot ;
 *   3. l'ordre des mots est libre, et ils peuvent venir de champs différents
 *      (« bellevue » du nom, « 33 » du code postal).
 */

/** Minuscules sans accents. `normalize('NFD')` décompose « é » en « e » suivi
 *  d'un accent combinant, que la plage U+0300 à U+036F retire. Elle est écrite
 *  en points de code : ces caractères sont invisibles dans un éditeur, et une
 *  copie qui en perdrait un désactiverait la règle sans bruit. */
export function normalize(valeur) {
  if (valeur === null || valeur === undefined) return '';
  return String(valeur)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
}

/** Découpe la saisie en mots. Une saisie vide rend un tableau vide, que les
 *  appelants lisent comme « aucun filtre », jamais comme « aucun résultat ». */
export function termes(recherche) {
  return normalize(recherche).split(/\s+/).filter(Boolean);
}

/**
 * Indique si une ligne répond à la recherche.
 * `fields` est la liste des valeurs cherchables de la ligne (nom, établissement,
 * ville, code postal…). Les valeurs nulles sont ignorées, pas converties en
 * « null » : taper « null » ferait sinon ressortir toutes les lignes
 * incomplètes.
 */
export function correspond(recherche, fields) {
  const words = termes(recherche);
  if (!words.length) return true;
  const haystack = fields.filter((v) => v !== null && v !== undefined && v !== '')
    .map(normalize).join(' ');
  return words.every((mot) => haystack.includes(mot));
}

/**
 * Filtre une liste. `fieldsOf` rend, pour une ligne, ses valeurs cherchables.
 * Une recherche vide rend la liste elle-même, pas une copie, pour qu'un `===`
 * entre la liste filtrée et la liste d'origine reste vrai.
 */
export function filterItems(items, recherche, fieldsOf) {
  if (!termes(recherche).length) return items;
  return items.filter((ligne) => correspond(recherche, fieldsOf(ligne)));
}

/** Localisation lisible d'une ligne : « 33000 Bordeaux », « Bordeaux », ou rien.
 *  Utilisé partout où une colonne « Localisation » s'affiche, pour que le même
 *  site ne s'écrive pas de deux façons selon l'écran. */
export function locationText(...chunks) {
  return chunks.filter(Boolean).join(' ');
}
